import React from 'react';
import { useSEO } from '../hooks/useSEO';
import { MapPin, Zap, FileText, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  useSEO({
    title: 'About PlanningSearch | UK Planning Application Intelligence',
    description: 'PlanningSearch helps builders, architects, and tradespeople discover planning applications and win more work using AI-powered insights.',
    canonical: 'https://planningsearch.com/about',
  });

  return (
    <div className="min-h-[calc(100vh-56px)] bg-white" data-testid="about-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-emerald-600 mb-2">About Us</p>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900 mb-4">
            Planning intelligence, built for the trade
          </h1>
          <p className="text-lg text-zinc-500 leading-relaxed">
            PlanningSearch was built to solve a simple problem: finding live building projects shouldn't require
            hours of trawling council websites.
          </p>
        </div>

        <div className="space-y-6 text-sm text-zinc-600 leading-relaxed">
          <p>
            Every week, thousands of planning applications are submitted across the UK — extensions,
            loft conversions, new builds, and commercial developments. Each one represents a potential project for
            builders, architects, surveyors, and tradespeople. But the data is scattered across dozens of council
            portals, formatted inconsistently, and nearly impossible to search at scale.
          </p>
          <p>
            PlanningSearch brings it all together. We aggregate planning data from local authorities, enrich it with
            AI-powered insights, and present it on a single interactive platform. Whether you're a sole trader
            looking for loft conversions in your postcode or a growing firm tracking commercial opportunities
            across multiple boroughs, PlanningSearch gives you the edge.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12">
          {[
            { icon: MapPin, title: 'Real-Time Map Search', desc: 'Browse live planning applications on an interactive map. Filter by type, status, council, and area.' },
            { icon: Zap, title: 'AI Intelligence', desc: 'Get AI-powered reports with opportunity scores, cost estimates, trade recommendations, and approach strategies.' },
            { icon: FileText, title: 'Direct Outreach', desc: 'Send professionally printed letters to applicants directly from the platform.' },
            { icon: Mail, title: 'Smart Alerts', desc: 'Get notified when new applications matching your criteria are submitted in your area.' },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="p-5 bg-zinc-50 border border-zinc-100 rounded-lg">
              <div className="w-8 h-8 bg-emerald-50 rounded-md flex items-center justify-center mb-3">
                <Icon className="w-4 h-4 text-emerald-700" />
              </div>
              <h3 className="font-['Manrope'] text-sm font-bold text-zinc-900 mb-1">{title}</h3>
              <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-10 border-t border-zinc-200">
          <h2 className="font-['Manrope'] text-xl font-bold text-zinc-900 mb-4">Get in Touch</h2>
          <p className="text-sm text-zinc-600 leading-relaxed mb-4">
            We'd love to hear from you — whether you're a user with feedback, a council looking to partner,
            or a developer interested in our API.
          </p>
          <div className="space-y-2 text-sm">
            <p className="text-zinc-600">
              <span className="font-medium text-zinc-800">General enquiries:</span>{' '}
              <a href="mailto:hello@planningsearch.com" className="text-emerald-600 hover:text-emerald-700">hello@planningsearch.com</a>
            </p>
            <p className="text-zinc-600">
              <span className="font-medium text-zinc-800">Support:</span>{' '}
              <a href="mailto:support@planningsearch.com" className="text-emerald-600 hover:text-emerald-700">support@planningsearch.com</a>
            </p>
          </div>
          <div className="mt-6">
            <Link to="/pricing" className="inline-flex items-center gap-1.5 bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-md text-sm hover:bg-emerald-800 transition-colors" data-testid="about-get-started">
              View Plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
